/* ============================================================================
 * LIVE_TV.EXE — the broadcast desk
 * ----------------------------------------------------------------------------
 * Built to the same shell as NEWS_DESK: `.dsp page-split` masthead with a crest,
 * titles and chips, a `.tabs page__tabs` strip, then one panel per module, then
 * a `.page__foot`.
 *
 * HONEST STATUS — read before assuming this broadcasts:
 *   • There is NO stream provider. No Livepeer, no Mux, no HLS ingest, no RTMP
 *     endpoint. Nothing originates here.
 *   • A "channel" is therefore a URL the operator already has, or nothing. The
 *     line-up below is what the desk can hold, not what is on air.
 *   • The player is real: it plays a supplied stream URL or a local file. The
 *     guide has no data source, so it says so rather than inventing a schedule.
 * ========================================================================= */

import { useCallback, useMemo, useRef, useState } from 'react';
import { appById } from './registry';
import { MediaPlayer, type PlayerStatus } from '../components/MediaPlayer';

type Tab = 'player' | 'channels' | 'guide' | 'about';

interface Channel {
  id: string;
  label: string;
  /** What this channel is for. */
  blurb: string;
  /** Null ⇒ no source known. Never a placeholder URL. */
  url: string | null;
}

/**
 * The line-up. `url: null` everywhere is the truthful state: these are the
 * channels the desk is built to carry, and no source has been provisioned.
 */
const CHANNELS: readonly Channel[] = [
  {
    id: 'main',
    label: 'SUPERCOMPUTE TV',
    blurb: 'The main station — tours, builds, and desk walkthroughs.',
    url: null,
  },
  {
    id: 'school',
    label: 'SCHOOL CHANNEL',
    blurb: 'Cohort sessions and Presence-backed lessons.',
    url: null,
  },
  {
    id: 'club',
    label: 'CLUB CHANNEL',
    blurb: 'Club nights, line-ups, and member streams.',
    url: null,
  },
  {
    id: 'council',
    label: 'COUNCIL CHAMBER',
    blurb: 'Governance sessions and charter readings.',
    url: null,
  },
];

const TV_TABS = [
  { id: 'player', label: 'Player' },
  { id: 'channels', label: 'Channels' },
  { id: 'guide', label: 'Guide' },
  { id: 'about', label: 'About' },
] as const;

export function LiveTv() {
  const [tab, setTab] = useState<Tab>('player');
  const [url, setUrl] = useState<string>('');
  const [src, setSrc] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [status, setStatus] = useState<PlayerStatus>('empty');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  const tune = useCallback((next: string, name: string | null) => {
    // Revoke the previous blob so a long session does not leak file handles.
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    }
    setSrc(next);
    setFileName(name);
    setStatus(next ? 'loading' : 'empty');
  }, []);

  const onPickFile = useCallback(
    (f: File | undefined) => {
      if (!f) return;
      const obj = URL.createObjectURL(f);
      objectUrl.current = obj;
      tune(obj, f.name);
    },
    [tune],
  );

  const provisioning = useMemo(
    () => CHANNELS.filter((c) => c.url).length,
    [],
  );

  return (
    <div className="stack">
      <div className="dsp page-split">
        <header className="page__head">
          <img
            className="page__crest"
            src={appById('live-tv')?.tile}
            width={40}
            height={40}
            alt=""
            aria-hidden="true"
          />
          <div className="page__titles">
            <p className="eyebrow">live tv · broadcast</p>
            <h1 className="h1">Everything we broadcast</h1>
          </div>
          <div className="page__chips">
            <span className="badge badge-admin">{TV_TABS.length} modules</span>
          </div>
        </header>

        <div className="tabs page__tabs" role="tablist" aria-label="Live TV modules">
          {TV_TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel">
        <div className="row">
          <span className="row__k">Provider</span>
          <span className="row__v">
            <span className="tag tag--warn">none wired</span>
          </span>
        </div>
        <div className="row">
          <span className="row__k">Source</span>
          <span className="row__v mono">
            {fileName ?? (src ? src.slice(0, 46) : <span className="tag tag--muted">nothing loaded</span>)}
          </span>
        </div>
        <div className="row">
          <span className="row__k">State</span>
          <span className="row__v">
            <span className="tag tag--muted">{status}</span>
          </span>
        </div>
      </div>

      {tab === 'player' && (
        <div className="stack">
          <MediaPlayer src={src || undefined} label="live tv" onStatus={setStatus} />

          <span className="eyebrow">source</span>
          <div className="hstack">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…/stream.m3u8 or any playable media URL"
              spellCheck={false}
              style={{
                flex: '1 1 auto',
                minWidth: 160,
                background: 'var(--ui-well, var(--ui-surface-2))',
                border: '1px solid var(--ui-border)',
                borderRadius: 'var(--ui-radius-ctl)',
                padding: '4px 6px',
                fontSize: 11,
              }}
            />
            <button
              type="button"
              className="btn"
              disabled={url.trim().length === 0}
              onClick={() => tune(url.trim(), null)}
            >
              Tune
            </button>
          </div>

          <div className="hstack">
            <input
              ref={fileRef}
              type="file"
              accept="video/*,audio/*"
              style={{ display: 'none' }}
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
            <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
              Choose a local file
            </button>
            <button
              type="button"
              className="btn"
              disabled={!src}
              onClick={() => {
                if (fileRef.current) fileRef.current.value = '';
                tune('', null);
              }}
            >
              Eject
            </button>
          </div>

          <p className="note">
            Playback is the browser&rsquo;s own. An unsupported format or an
            unreachable URL shows the element&rsquo;s error on the frame — it is
            not hidden. Nothing autoplays.
          </p>
        </div>
      )}

      {tab === 'channels' && (
        <div className="stack">
          <div className="hstack" style={{ justifyContent: 'space-between' }}>
            <span className="eyebrow">line-up</span>
            <span className="tag tag--warn">
              {provisioning} of {CHANNELS.length} provisioned
            </span>
          </div>
          {CHANNELS.map((c) => (
            <div className="panel" key={c.id}>
              <div className="row">
                <span className="row__k">Channel</span>
                <span className="row__v">{c.label}</span>
              </div>
              <div className="row">
                <span className="row__k">Purpose</span>
                <span className="row__v">{c.blurb}</span>
              </div>
              <div className="row">
                <span className="row__k">Source</span>
                <span className="row__v mono">
                  {c.url ?? <span className="tag tag--warn">no feed</span>}
                </span>
              </div>
              <div className="hstack" style={{ marginTop: 6 }}>
                <button
                  type="button"
                  className="btn"
                  disabled={!c.url}
                  onClick={() => {
                    if (!c.url) return;
                    setUrl(c.url);
                    tune(c.url, null);
                    setTab('player');
                  }}
                >
                  Tune
                </button>
              </div>
            </div>
          ))}
          <p className="note note--warn">
            Every channel reads <strong>no feed</strong> because no source has
            been provisioned. These are the stations the desk is built to carry,
            not stations that are on air.
          </p>
        </div>
      )}

      {tab === 'guide' && (
        <div className="stack">
          <div className="panel">
            <div className="row">
              <span className="row__k">Schedule source</span>
              <span className="row__v">
                <span className="tag tag--warn">none</span>
              </span>
            </div>
            <div className="row">
              <span className="row__k">Entries</span>
              <span className="row__v">
                <span className="tag tag--muted">
                  0 — no schedule is published
                </span>
              </span>
            </div>
          </div>
          <p className="note">
            There is no programme guide, and none is invented. A guide needs a
            published schedule; when one exists it belongs here with its source
            named, the same way a failed read is shown rather than a blank.
          </p>
        </div>
      )}

      {tab === 'about' && (
        <div className="stack">
          <div className="panel">
            <div className="row">
              <span className="row__k">Desk</span>
              <span className="row__v mono">live-tv</span>
            </div>
            <div className="row">
              <span className="row__k">Role</span>
              <span className="row__v">Playback surface for broadcast output</span>
            </div>
            <div className="row">
              <span className="row__k">Provider</span>
              <span className="row__v">
                <span className="tag tag--warn">
                  none — Livepeer / Mux not wired
                </span>
              </span>
            </div>
            <div className="row">
              <span className="row__k">Player</span>
              <span className="row__v">
                <span className="tag tag--ok">real — plays supplied sources</span>
              </span>
            </div>
          </div>
          <p className="note">
            CHANNEL.EXE holds the syndication and social side. This desk is the
            screen: it plays what it is given and reports what it cannot.
          </p>
        </div>
      )}

      <div className="dsp">
        <p className="page__foot">
          A broadcast has two halves: something that originates it, and something
          that plays it. Only the second half exists here.
        </p>
      </div>
    </div>
  );
}
